import { nanoid } from "nanoid";
import { navigate } from "./router";

export function renderLanding(root: HTMLElement): () => void {
  const wrap = el("div", { class: "landing" });

  const hero = el("section", { class: "hero" });

  const brand = el("div", { class: "brand" });
  const mark = el("div", { class: "brand-mark" });
  for (let i = 0; i < 5; i++) mark.appendChild(el("span"));
  brand.appendChild(mark);
  brand.appendChild(el("div", { class: "brand-name" }, "StreamLevels"));
  hero.appendChild(brand);

  hero.appendChild(
    el("h1", {}, "Match your mic level before you go live."),
  );
  hero.appendChild(
    el(
      "p",
      { class: "lead" },
      "Spin up a lobby, share the link with your co-stream, and watch each other's input levels in real time. Adjust your mic gain in OS / OBS until everyone sits in the same range. No audio is ever transmitted — only level data.",
    ),
  );

  const cta = el("div", { class: "cta-row" });
  const createBtn = el("button", {}, "Create a lobby") as HTMLButtonElement;
  createBtn.addEventListener("click", () => {
    const id = nanoid(8);
    navigate(`/r/${id}`);
  });
  cta.appendChild(createBtn);
  hero.appendChild(cta);

  hero.appendChild(
    el(
      "p",
      { class: "footnote" },
      "Lobbies are ephemeral. Close the tab and the room dissolves.",
    ),
  );

  wrap.appendChild(hero);

  const how = el("section", { class: "how" });
  how.appendChild(
    card(
      "01",
      "Create a lobby",
      "One click. You get a short shareable URL — paste it to your co-streamers.",
    ),
  );
  how.appendChild(
    card(
      "02",
      "Grant mic access",
      "Each participant picks their input device. Your audio stays on your machine — only the level value travels.",
    ),
  );
  how.appendChild(
    card(
      "03",
      "Match the bars",
      "Speak at your normal volume. Adjust gain (OBS / Audio MIDI / mic preamp) until everyone peaks in the same yellow zone.",
    ),
  );
  wrap.appendChild(how);

  const stats = el("section", { class: "stats" });
  const statBoxes = [
    statBox("Rooms active now", "active"),
    statBox("Total rooms created", "created"),
    statBox("Total streamers", "streamers"),
  ];
  for (const { node } of statBoxes) stats.appendChild(node);
  wrap.appendChild(stats);

  let cancelled = false;
  const refresh = async () => {
    try {
      const res = await fetch("/api/stats", { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as {
        activeRooms: number;
        roomsCreated: number;
        streamersTotal: number;
      };
      if (cancelled) return;
      statBoxes[0].set(data.activeRooms);
      statBoxes[1].set(data.roomsCreated);
      statBoxes[2].set(data.streamersTotal);
    } catch {
      /* offline — leave placeholders */
    }
  };
  void refresh();
  const interval = window.setInterval(refresh, 10_000);

  root.appendChild(wrap);
  return () => {
    cancelled = true;
    window.clearInterval(interval);
  };
}

function statBox(
  label: string,
  _key: string,
): { node: HTMLElement; set: (n: number) => void } {
  const node = el("div", { class: "stat" });
  const value = el("div", { class: "stat-value" }, "—");
  node.appendChild(value);
  node.appendChild(el("div", { class: "stat-label" }, label));
  let last = -1;
  return {
    node,
    set(n: number) {
      if (n === last) return;
      last = n;
      value.textContent = formatStat(n);
    },
  };
}

function formatStat(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function card(step: string, title: string, body: string): HTMLElement {
  const c = el("div", { class: "card" });
  c.appendChild(el("div", { class: "step" }, step));
  c.appendChild(el("h3", {}, title));
  c.appendChild(el("p", {}, body));
  return c;
}

function el(
  tag: string,
  attrs: Record<string, string> = {},
  text?: string,
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}
